// src/pages/Listings.js

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Chip,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useOutletContext } from "react-router-dom";

import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";

import { apiFetch } from "../api/http"; // ✅ token-aware helper
import { API_BASE_URL, API_ENDPOINTS } from "../config";

// Endpoints using centralized config
const LISTINGS_API_URL = `${API_BASE_URL}${API_ENDPOINTS.LISTINGS}`;
const LISTING_FETCH_API_URL = `${API_BASE_URL}/api/listings/fetch`;
const CLIENTS_API_URL = `${API_BASE_URL}${API_ENDPOINTS.CLIENTS}`;
const CHAT_API_URL = `${API_BASE_URL}${API_ENDPOINTS.CHAT}`;

export {
  LISTINGS_API_URL,
  LISTING_FETCH_API_URL,
  CLIENTS_API_URL,
  CHAT_API_URL,
};

// Includes Pending + Sold
const STATUS_OPTIONS = [
  "Active",
  "Coming Soon",
  "Under Contract",
  "Pending",
  "Closed",
  "Sold",
  "Expired",
  "Withdrawn",
];

function formatCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `$${Number(value).toLocaleString()}`;
}

// Map raw scraped status into our STATUS_OPTIONS
function normalizeScrapedStatus(raw) {
  if (!raw) return null;
  const s = raw.toLowerCase();

  if (s.includes("sold") || s.includes("closed")) return "Sold";
  if (s.includes("pending") || s.includes("contingent")) return "Pending";
  if (s.includes("under contract")) return "Under Contract";
  if (s.includes("active") || s.includes("for sale")) return "Active";

  return null;
}

// Choose first URL to show / click
function deriveExternalUrl(listing) {
  return listing.zillow_url || listing.realtor_url || listing.homes_url || "";
}

export default function ListingsPage() {
  const theme = useTheme();
  const { onAiStatusChange, aiRefreshToken } = useOutletContext() || {};

  const [listings, setListings] = useState([]);
  const [clients, setClients] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [statusFilter, setStatusFilter] = useState("All");
  const [clientFilter, setClientFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sortKey, setSortKey] = useState("newest");

  const [selectedListingId, setSelectedListingId] = useState(null);

  // create / edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingListing, setEditingListing] = useState(null);

  // Auto-fill from URL
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const [autoFillError, setAutoFillError] = useState("");

  // AI suggestions
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiText, setAiText] = useState("");

  // ---------------- Load listings + clients (JWT aware) ----------------
  const loadData = useCallback(
    async () => {
      try {
        setLoading(true);
        setLoadError("");
        onAiStatusChange?.({ loading: true });

        const [listingsRes, clientsRes] = await Promise.all([
          apiFetch("/api/listings/"),
          apiFetch("/api/clients/"),
        ]);

        if (!listingsRes.ok) {
          throw new Error(`Failed to load listings (${listingsRes.status})`);
        }
        if (!clientsRes.ok) {
          throw new Error(`Failed to load clients (${clientsRes.status})`);
        }

        const listingsJson = await listingsRes.json();
        const clientsJson = await clientsRes.json();

        setListings(listingsJson || []);
        setClients(clientsJson || []);

        if (!selectedListingId && (listingsJson || []).length > 0) {
          setSelectedListingId(listingsJson[0].id);
        }
      } catch (err) {
        console.error("Listings load error:", err);
        setLoadError(err.message || "Unable to load listings.");
      } finally {
        setLoading(false);
        onAiStatusChange?.({
          loading: false,
          lastUpdated: new Date().toISOString(),
        });
      }
    },
    [onAiStatusChange, selectedListingId]
  );

  useEffect(() => {
    loadData();
  }, [loadData, aiRefreshToken]);

  // ensure selection remains valid when data changes
  useEffect(() => {
    if (!listings.length) {
      setSelectedListingId(null);
      return;
    }
    const exists = listings.some((l) => l.id === selectedListingId);
    if (!exists) {
      setSelectedListingId(listings[0].id);
    }
  }, [listings, selectedListingId]);

  // ---------------- Derived helpers ----------------
  const clientById = useMemo(() => {
    const map = new Map();
    (clients || []).forEach((c) => map.set(c.id, c));
    return map;
  }, [clients]);

  const annotatedListings = useMemo(
    () =>
      (listings || []).map((l) => {
        const client = l.client || clientById.get(l.client_id) || null;
        const ownerName =
          (client
            ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
            : null) || "Unknown client";
        return {
          ...l,
          ownerName,
          external_url: deriveExternalUrl(l),
        };
      }),
    [listings, clientById]
  );

  // Filters + sort
  const filteredListings = useMemo(() => {
    let list = [...annotatedListings];

    if (statusFilter !== "All") {
      list = list.filter((l) => (l.status || "Active") === statusFilter);
    }

    if (clientFilter !== "All") {
      const id = Number(clientFilter);
      list = list.filter((l) => Number(l.client_id) === id);
    }

    if (minPrice) {
      const min = Number(minPrice);
      list = list.filter(
        (l) => l.list_price != null && Number(l.list_price) >= min
      );
    }
    if (maxPrice) {
      const max = Number(maxPrice);
      list = list.filter(
        (l) => l.list_price != null && Number(l.list_price) <= max
      );
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter((l) => {
        const addr = (l.address || "").toLowerCase();
        const mls = (l.mls_id || "").toLowerCase();
        const owner = (l.ownerName || "").toLowerCase();
        return (
          addr.includes(term) || mls.includes(term) || owner.includes(term)
        );
      });
    }

    list.sort((a, b) => {
      switch (sortKey) {
        case "newest":
          return (
            new Date(b.created_at || 0).getTime() -
            new Date(a.created_at || 0).getTime()
          );
        case "oldest":
          return (
            new Date(a.created_at || 0).getTime() -
            new Date(b.created_at || 0).getTime()
          );
        case "price_high":
          return (b.list_price || 0) - (a.list_price || 0);
        case "price_low":
          return (a.list_price || 0) - (b.list_price || 0);
        default:
          return 0;
      }
    });

    return list;
  }, [
    annotatedListings,
    statusFilter,
    clientFilter,
    searchTerm,
    minPrice,
    maxPrice,
    sortKey,
  ]);

  const selectedListing = useMemo(
    () => filteredListings.find((l) => l.id === selectedListingId) || null,
    [filteredListings, selectedListingId]
  );

  // KPIs
  const kpis = useMemo(() => {
    if (!annotatedListings.length) {
      return {
        total: 0,
        active: 0,
        underContract: 0,
        closed: 0,
        activeVolume: 0,
      };
    }

    const isActive = (status) =>
      ["Active", "Coming Soon", "Under Contract", "Pending"].includes(
        status || "Active"
      );

    let active = 0;
    let underContract = 0;
    let closed = 0;
    let activeVolume = 0;

    annotatedListings.forEach((l) => {
      const status = l.status || "Active";
      const price = l.list_price || 0;

      if (isActive(status)) {
        active += 1;
        activeVolume += price;
      }
      if (status === "Under Contract" || status === "Pending") {
        underContract += 1;
      }
      // Treat Closed + Sold as closed
      if (status === "Closed" || status === "Sold") {
        closed += 1;
      }
    });

    return {
      total: annotatedListings.length,
      active,
      underContract,
      closed,
      activeVolume,
    };
  }, [annotatedListings]);

  // status chart
  const statusChartData = useMemo(() => {
    const counts = {};
    annotatedListings.forEach((l) => {
      const key = l.status || "Active";
      counts[key] = (counts[key] || 0) + 1;
    });

    const order = [
      "Coming Soon",
      "Active",
      "Under Contract",
      "Pending",
      "Closed",
      "Sold",
      "Expired",
      "Withdrawn",
    ];

    return order
      .filter((s) => counts[s])
      .map((s) => ({ status: s, count: counts[s] || 0 }));
  }, [annotatedListings]);

  // ---------------- Create / edit listing ----------------
  const openNewListing = () => {
    setEditingListing({
      id: null,
      client_id: "",
      address: "",
      city: "",
      state: "",
      postal_code: "",
      list_price: "",
      sold_price: "",
      status: "Active",
      side: "",
      property_type: "",
      beds: "",
      baths: "",
      sqft: "",
      lot_size_sqft: "",
      mls_id: "",
      zillow_url: "",
      realtor_url: "",
      homes_url: "",
      headline: "",
      notes: "",
      external_url: "", // UI-only
    });
    setAutoFillError("");
    setEditOpen(true);
  };

  const openEditListing = () => {
    if (!selectedListing) return;
    setEditingListing({
      ...selectedListing,
      client_id: selectedListing.client_id ?? "",
      list_price: selectedListing.list_price ?? "",
      sold_price: selectedListing.sold_price ?? "",
      beds: selectedListing.beds ?? "",
      baths: selectedListing.baths ?? "",
      sqft: selectedListing.sqft ?? "",
      lot_size_sqft: selectedListing.lot_size_sqft ?? "",
      external_url: selectedListing.external_url || "",
    });
    setAutoFillError("");
    setEditOpen(true);
  };

  const handleChangeEditField = (field) => (e) => {
    const value = e.target.value;
    setEditingListing((prev) => ({ ...prev, [field]: value }));
  };

  // Decide which URL field to set based on hostname
  function buildUrlFieldsFromExternal(externalUrl) {
    let zillow_url = null;
    let realtor_url = null;
    let homes_url = null;

    if (!externalUrl) {
      return { zillow_url, realtor_url, homes_url };
    }

    const trimmed = externalUrl.trim();
    try {
      const host = new URL(trimmed).hostname.toLowerCase();
      if (host.includes("zillow.com")) {
        zillow_url = trimmed;
      } else if (host.includes("realtor.com")) {
        realtor_url = trimmed;
      } else if (host.includes("homes.com")) {
        homes_url = trimmed;
      } else {
        // default bucket if unknown
        zillow_url = trimmed;
      }
    } catch {
      // If it's not a valid URL, just shove it into zillow_url
      zillow_url = trimmed;
    }

    return { zillow_url, realtor_url, homes_url };
  }

  // Auto-fill from URL using /api/listings/fetch
  const handleAutoFillFromUrl = async () => {
    if (!editingListing?.external_url) return;

    setAutoFillLoading(true);
    setAutoFillError("");

    try {
      const res = await apiFetch("/api/listings/fetch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: editingListing.external_url }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Auto-fill failed (${res.status})`);
      }

      const data = await res.json();

      const normalizedStatus = normalizeScrapedStatus(data.status);

      setEditingListing((prev) => ({
        ...prev,
        address: data.address ?? prev.address,
        city: data.city ?? prev.city,
        state: data.state ?? prev.state,
        postal_code: data.zip_code ?? prev.postal_code,
        list_price:
          data.list_price != null
            ? data.list_price
            : data.price != null
            ? data.price
            : prev.list_price,
        beds: data.bedrooms != null ? data.bedrooms : prev.beds,
        baths: data.bathrooms != null ? data.bathrooms : prev.baths,
        sqft: data.square_feet != null ? data.square_feet : prev.sqft,
        status: normalizedStatus || prev.status,
        external_url: data.source_url || prev.external_url,
      }));
    } catch (err) {
      console.error("Auto-fill error:", err);
      setAutoFillError(
        err.message || "Unable to auto-fill listing from URL."
      );
    } finally {
      setAutoFillLoading(false);
    }
  };

  const handleSaveListing = async () => {
    if (!editingListing || !editingListing.address) return;

    const isNew = !editingListing.id;

    const { zillow_url, realtor_url, homes_url } =
      buildUrlFieldsFromExternal(editingListing.external_url);

    const payload = {
      client_id:
        editingListing.client_id && editingListing.client_id !== ""
          ? Number(editingListing.client_id)
          : null,

      address: editingListing.address,
      city: editingListing.city || null,
      state: editingListing.state || null,
      postal_code: editingListing.postal_code || null,

      property_type: editingListing.property_type || null,
      beds:
        editingListing.beds !== "" && editingListing.beds != null
          ? Number(editingListing.beds)
          : null,
      baths:
        editingListing.baths !== "" && editingListing.baths != null
          ? Number(editingListing.baths)
          : null,
      sqft:
        editingListing.sqft !== "" && editingListing.sqft != null
          ? Number(editingListing.sqft)
          : null,
      lot_size_sqft:
        editingListing.lot_size_sqft !== "" &&
        editingListing.lot_size_sqft != null
          ? Number(editingListing.lot_size_sqft)
          : null,

      list_price:
        editingListing.list_price !== "" &&
        editingListing.list_price != null
          ? Number(editingListing.list_price)
          : null,
      sold_price:
        editingListing.sold_price !== "" &&
        editingListing.sold_price != null
          ? Number(editingListing.sold_price)
          : null,

      status: editingListing.status || "Active",
      side: editingListing.side || null,

      mls_id: editingListing.mls_id || null,
      zillow_url,
      realtor_url,
      homes_url,

      headline: editingListing.headline || null,
      notes: editingListing.notes || null,
    };

    try {
      setSaving(true);
      const url = isNew
        ? "/api/listings/"
        : `/api/listings/${editingListing.id}`;
      const method = isNew ? "POST" : "PUT";

      const res = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Failed to save listing (${res.status})`);
      }

      await loadData();
      setEditOpen(false);
    } catch (err) {
      console.error("Save listing error:", err);
      alert(err.message || "Unable to save listing.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteListing = async () => {
    if (!selectedListing) return;
    if (
      !window.confirm(
        `Delete listing at "${selectedListing.address}"? This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const res = await apiFetch(`/api/listings/${selectedListing.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        throw new Error(`Failed to delete listing (${res.status})`);
      }
      await loadData();
    } catch (err) {
      console.error("Delete listing error:", err);
      alert(err.message || "Unable to delete listing.");
    }
  };

  // ---------------- AI listing strategy ----------------
  const handleAiStrategy = async () => {
    if (!selectedListing) {
      alert("Select a listing first.");
      return;
    }
    setAiError("");
    setAiBusy(true);
    onAiStatusChange?.({ loading: true });

    try {
      const payload = {
        address: selectedListing.address,
        mls_id: selectedListing.mls_id,
        price: selectedListing.list_price,
        status: selectedListing.status,
        created_at: selectedListing.created_at,
        ownerName: selectedListing.ownerName,
        external_url: selectedListing.external_url,
      };

      const messages = [
        {
          role: "system",
          content:
            "You are AgentBlayna, an AI listing strategist for real estate agents. " +
            "You receive a single listing with address, price, status, owner name, and optional online link. " +
            "Give concise, practical advice: pricing positioning, next 3–5 marketing actions, " +
            "and what conversations the agent should have with the seller this week.",
        },
        {
          role: "user",
          content: JSON.stringify(payload),
        },
      ];

      const res = await apiFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "gpt-4o-mini", messages }),
      });

      if (!res.ok) {
        throw new Error(`AI HTTP ${res.status}`);
      }
      const data = await res.json();
      const reply =
        typeof data?.reply === "string"
          ? data.reply
          : typeof data?.reply?.content === "string"
          ? data.reply.content
          : "I couldn't generate a listing strategy. Try again in a bit.";
      setAiText(reply);
    } catch (err) {
      console.error("AI listing error:", err);
      setAiError(
        "AgentBlayna could not reach the AI engine. Check backend logs."
      );
    } finally {
      setAiBusy(false);
      onAiStatusChange?.({
        loading: false,
        lastUpdated: new Date().toISOString(),
      });
    }
  };

  // ---------------- Render ----------------
  return (
    <Box sx={{ p: 1, pb: 4 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        mb={2}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <HomeWorkRoundedIcon sx={{ color: theme.palette.primary.main, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} noWrap>
              Listings & Inventory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Every listing, its status, and its owner in one clean view.
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterAltRoundedIcon />}
            onClick={() => {
              setStatusFilter("All");
              setClientFilter("All");
              setSearchTerm("");
              setMinPrice("");
              setMaxPrice("");
              setSortKey("newest");
            }}
          >
            Clear filters
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={openNewListing}
          >
            New Listing
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        {/* Left column – KPIs + filters + listing list */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {/* KPI row */}
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Active listings
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {kpis.active}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Anything currently on the market.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Under contract / pending
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {kpis.underContract}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Deals closest to the closing table.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Closed listings
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {kpis.closed}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Great for testimonials & referrals.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AttachMoneyRoundedIcon
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Active volume
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {formatCurrency(kpis.activeVolume)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Total price of active & UC listings.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Filters */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Filters
                </Typography>
                <TextField
                  size="small"
                  label="Search address / MLS / client"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="All">All statuses</MenuItem>
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <TextField
                  size="small"
                  select
                  label="Client"
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="All">All clients</MenuItem>
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {`${c.first_name || ""} ${c.last_name || ""}`.trim() ||
                        "(No name)"}
                    </MenuItem>
                  ))}
                </TextField>

                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Min price"
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Max price"
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <TextField
                  size="small"
                  select
                  label="Sort by"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="newest">Newest first</MenuItem>
                  <MenuItem value="oldest">Oldest first</MenuItem>
                  <MenuItem value="price_high">Price (high → low)</MenuItem>
                  <MenuItem value="price_low">Price (low → high)</MenuItem>
                </TextField>
              </Stack>
            </Paper>

            {/* Listing list */}
            <Paper
              sx={{
                p: 1.5,
                maxHeight: 420,
                overflowY: "auto",
                borderRadius: 2,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Listings ({filteredListings.length})
                </Typography>
                <Chip
                  size="small"
                  label={`${kpis.total} total`}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  }}
                />
              </Stack>
              <Divider sx={{ mb: 1 }} />

              {loading && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 1 }}
                >
                  Loading listings…
                </Typography>
              )}
              {!loading && loadError && (
                <Typography variant="body2" color="error" sx={{ p: 1 }}>
                  {loadError}
                </Typography>
              )}
              {!loading &&
                !loadError &&
                filteredListings.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 1 }}
                  >
                    No listings match the current filters.
                  </Typography>
                )}

              {filteredListings.map((l) => {
                const isSelected = l.id === selectedListingId;
                return (
                  <React.Fragment key={l.id}>
                    <Box
                      sx={{
                        py: 0.75,
                        px: 0.75,
                        borderRadius: 1.5,
                        cursor: "pointer",
                        bgcolor: isSelected
                          ? alpha(theme.palette.primary.main, 0.06)
                          : "transparent",
                        "&:hover": {
                          bgcolor: alpha(
                            theme.palette.primary.main,
                            0.08
                          ),
                        },
                      }}
                      onClick={() => setSelectedListingId(l.id)}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={isSelected ? 700 : 600}
                            noWrap
                          >
                            {l.address}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {l.ownerName || "No client linked"}
                            {l.mls_id ? ` • MLS ${l.mls_id}` : ""}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {formatCurrency(l.list_price)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: "uppercase" }}
                          >
                            {l.status || "Active"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </Paper>
          </Stack>
        </Grid>

        {/* Right column – chart + listing detail + AI */}
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            {/* Status chart */}
            <Paper sx={{ p: 2, height: 260, borderRadius: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <HomeWorkRoundedIcon fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Listings by status
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Quick health check of your inventory
                </Typography>
              </Stack>
              {statusChartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Add a few listings and you&apos;ll see your inventory mix
                  here.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart data={statusChartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={alpha(theme.palette.divider, 0.8)}
                    />
                    <XAxis
                      dataKey="status"
                      tick={{
                        fontSize: 11,
                        fill: theme.palette.text.secondary,
                      }}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{
                        fontSize: 11,
                        fill: theme.palette.text.secondary,
                      }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 8,
                        borderColor: theme.palette.divider,
                        backgroundColor: theme.palette.background.paper,
                        fontSize: 12,
                      }}
                    />
                    <Bar
                      dataKey="count"
                      name="Listings"
                      radius={[6, 6, 0, 0]}
                      fill={theme.palette.primary.main}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>

            {/* Listing detail */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              {!selectedListing ? (
                <Typography variant="body2" color="text.secondary">
                  Select a listing on the left to see details.
                </Typography>
              ) : (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={1}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {selectedListing.address}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {selectedListing.ownerName
                          ? `Client: ${selectedListing.ownerName}`
                          : "No client linked yet"}
                        {selectedListing.mls_id
                          ? ` • MLS ${selectedListing.mls_id}`
                          : ""}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={selectedListing.status || "Active"}
                          sx={{
                            fontWeight: 600,
                            bgcolor: alpha(
                              theme.palette.primary.main,
                              0.06
                            ),
                            color: theme.palette.primary.main,
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{ ml: 0.5 }}
                        >
                          {formatCurrency(selectedListing.list_price)}
                        </Typography>
                      </Stack>
                      {selectedListing.status === "Sold" &&
                        selectedListing.sold_price != null && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            Sold for{" "}
                            {formatCurrency(selectedListing.sold_price)}
                          </Typography>
                        )}
                    </Box>

                    <Stack direction="row" spacing={1}>
                      {selectedListing.external_url && (
                        <Tooltip title="Open listing (Zillow / Realtor / Homes)">
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(
                                selectedListing.external_url,
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            <OpenInNewRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit listing">
                        <IconButton size="small" onClick={openEditListing}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete listing">
                        <IconButton
                          size="small"
                          onClick={handleDeleteListing}
                        >
                          <DeleteRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Use this space as your single source of truth for the
                    listing: pricing changes, marketing actions, and the
                    conversations you&apos;re having with the seller.
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Tip: Paste your Zillow, Realtor.com, or Homes.com URL in
                    the &quot;Online listing URL&quot; field and click
                    &quot;Auto-fill&quot; to pull in price, address, and basic
                    details.
                  </Typography>
                </>
              )}
            </Paper>

            {/* AI strategy card */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <AutoAwesomeRoundedIcon
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography variant="subtitle2" fontWeight={600}>
                    AgentBlayna – Listing game plan
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAiStrategy}
                  disabled={aiBusy || !selectedListing}
                >
                  {aiBusy ? "Thinking…" : "Generate plan"}
                </Button>
              </Stack>

              {!selectedListing && (
                <Typography variant="body2" color="text.secondary">
                  Pick a listing and AgentBlayna will suggest pricing position,
                  marketing moves, and what to cover with your seller this
                  week.
                </Typography>
              )}

              {selectedListing && aiError && (
                <Typography variant="body2" color="error">
                  {aiError}
                </Typography>
              )}

              {selectedListing && !aiError && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {aiBusy
                    ? "Analyzing the listing and market positioning…"
                    : aiText ||
                      "Click “Generate plan” to get a short, tactical game plan you can turn into calls, texts, and updates today."}
                </Typography>
              )}
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Create / Edit dialog */}
      <Dialog
        open={editOpen}
        onClose={() => !saving && setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingListing && editingListing.id ? "Edit listing" : "New listing"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {editingListing && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  label="Property address"
                  fullWidth
                  required
                  value={editingListing.address}
                  onChange={handleChangeEditField("address")}
                  placeholder="123 Main St"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="City"
                  fullWidth
                  value={editingListing.city || ""}
                  onChange={handleChangeEditField("city")}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="State"
                  fullWidth
                  value={editingListing.state || ""}
                  onChange={handleChangeEditField("state")}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="ZIP"
                  fullWidth
                  value={editingListing.postal_code || ""}
                  onChange={handleChangeEditField("postal_code")}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="List price"
                  type="number"
                  fullWidth
                  value={editingListing.list_price}
                  onChange={handleChangeEditField("list_price")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="MLS ID"
                  fullWidth
                  value={editingListing.mls_id || ""}
                  onChange={handleChangeEditField("mls_id")}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Status"
                  fullWidth
                  value={editingListing.status || "Active"}
                  onChange={handleChangeEditField("status")}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Only show sold price if status is Sold */}
              {editingListing.status === "Sold" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sold price"
                    type="number"
                    fullWidth
                    value={editingListing.sold_price}
                    onChange={handleChangeEditField("sold_price")}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Client / owner"
                  fullWidth
                  value={editingListing.client_id || ""}
                  onChange={handleChangeEditField("client_id")}
                >
                  <MenuItem value="">No client linked</MenuItem>
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {`${c.first_name || ""} ${c.last_name || ""}`.trim() ||
                        "(No name)"}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Property type"
                  fullWidth
                  value={editingListing.property_type || ""}
                  onChange={handleChangeEditField("property_type")}
                />
              </Grid>

              <Grid item xs={4} sm={4}>
                <TextField
                  label="Beds"
                  type="number"
                  fullWidth
                  value={editingListing.beds}
                  onChange={handleChangeEditField("beds")}
                />
              </Grid>
              <Grid item xs={4} sm={4}>
                <TextField
                  label="Baths"
                  type="number"
                  fullWidth
                  value={editingListing.baths}
                  onChange={handleChangeEditField("baths")}
                />
              </Grid>
              <Grid item xs={4} sm={4}>
                <TextField
                  label="Sq Ft"
                  type="number"
                  fullWidth
                  value={editingListing.sqft}
                  onChange={handleChangeEditField("sqft")}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label="Online listing URL (Zillow, Realtor, Homes, etc.)"
                    fullWidth
                    value={editingListing.external_url || ""}
                    onChange={handleChangeEditField("external_url")}
                    onBlur={handleAutoFillFromUrl}
                    placeholder="https://www.zillow.com/homedetails/..."
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAutoFillFromUrl}
                    disabled={autoFillLoading || !editingListing.external_url}
                    sx={{ whiteSpace: "nowrap", alignSelf: "center" }}
                  >
                    {autoFillLoading ? "Auto-filling…" : "Auto-fill"}
                  </Button>
                </Stack>
                {autoFillError && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {autoFillError}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Headline"
                  fullWidth
                  value={editingListing.headline || ""}
                  onChange={handleChangeEditField("headline")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  minRows={3}
                  value={editingListing.notes || ""}
                  onChange={handleChangeEditField("notes")}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => !saving && setEditOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveListing}
            disabled={
              saving || !editingListing || !editingListing.address.trim()
            }
          >
            {saving ? "Saving…" : "Save listing"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
